import React, { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { IconUserPlus } from '@tabler/icons-react'
import { BackButton } from 'components'
import { PageTitle } from 'context/PageTitleContext'
import { addCompanyUser } from 'services/clients.svc'
import {
  PLACEHOLDER_EMAIL,
  PLACEHOLDER_FIRSTNAME,
  PLACEHOLDER_SURNAME,
  PLACEHOLDER_PHONE,
} from '@paramount-prestart/shared/placeholders'
import './AddCompanyUser.scss'

const validate = ({ firstName, surname, email, phone }) => {
  const errors = {}
  if (!firstName?.trim()) errors.firstName = 'First name is required'
  if (!surname?.trim()) errors.surname = 'Surname is required'
  if (!email?.trim()) {
    errors.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Please enter a valid email address'
  }
  if (!phone?.trim()) errors.phone = 'Phone is required'
  return errors
}

export default function AddCompanyUser() {
  const { companyId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [fields, setFields] = useState({ firstName: '', surname: '', email: '', phone: '' })
  const errors = useMemo(() => validate(fields), [fields])
  const canSubmit = Object.keys(errors).length === 0

  const set = (key) => (e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      addCompanyUser(companyId, {
        firstName: fields.firstName.trim(),
        surname: fields.surname.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Company user added successfully')
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
      navigate(-1)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to add company user')
      console.error('Failed to add company user', err)
    },
  })

  return (
    <div className="add-company-user">
      <PageTitle title="Company Profile" />
      <BackButton />
      <div className="acu-card">
        <div className="acu-header">
          <IconUserPlus size={18} stroke={1.5} />
          Add Company User
        </div>
        <p className="acu-description">
          The user will receive an email notification to access the portal.
        </p>
        <div className="acu-grid">
          <div className="acu-field">
            <label htmlFor="acu-firstName">First Name *</label>
            <input
              id="acu-firstName"
              type="text"
              autoComplete="off"
              value={fields.firstName}
              aria-invalid={fields.firstName ? Boolean(errors.firstName) : 'none'}
              placeholder={PLACEHOLDER_FIRSTNAME}
              onChange={set('firstName')}
            />
            {fields.firstName && errors.firstName && <small>{errors.firstName}</small>}
          </div>
          <div className="acu-field">
            <label htmlFor="acu-surname">Surname *</label>
            <input
              id="acu-surname"
              type="text"
              autoComplete="off"
              value={fields.surname}
              aria-invalid={fields.surname ? Boolean(errors.surname) : 'none'}
              placeholder={PLACEHOLDER_SURNAME}
              onChange={set('surname')}
            />
            {fields.surname && errors.surname && <small>{errors.surname}</small>}
          </div>
          <div className="acu-field">
            <label htmlFor="acu-email">Email Address *</label>
            <input
              id="acu-email"
              type="email"
              autoComplete="off"
              value={fields.email}
              aria-invalid={fields.email ? Boolean(errors.email) : 'none'}
              placeholder={PLACEHOLDER_EMAIL}
              onChange={set('email')}
            />
            {fields.email && errors.email && <small>{errors.email}</small>}
          </div>
          <div className="acu-field">
            <label htmlFor="acu-phone">Phone *</label>
            <input
              id="acu-phone"
              type="text"
              autoComplete="off"
              value={fields.phone}
              aria-invalid={fields.phone ? Boolean(errors.phone) : 'none'}
              placeholder={PLACEHOLDER_PHONE}
              onChange={set('phone')}
            />
            {fields.phone && errors.phone && <small>{errors.phone}</small>}
          </div>
        </div>
        <button
          className="acu-submit-btn"
          type="button"
          onClick={() => mutate()}
          disabled={!canSubmit || isPending}
          aria-busy={isPending}
          aria-label="Add company user"
        >
          <IconUserPlus size={15} stroke={2} />
          {isPending ? 'Please wait…' : 'Add User'}
        </button>
      </div>
    </div>
  )
}
